import React, { useEffect, useState } from 'react'
import { Box, Typography, Link } from '@mui/material'
import Badge from 'components/Badge'
import Snackbar from 'components/Snackbar'
import DisclosureDialog from 'components/DisclosureDialog'
import VehicleDetails from 'components/VehicleDetails'
import PurchaseDetails from 'components/PurchaseDetails'
import Spinner from 'components/Spinner'
import PdsLayout from 'layouts/PdsLayout'
import { useDealData } from 'hooks/useDealData'
import { useDynamicManageStore } from 'store/useDynamicManageStore'
import { useNotificationStore } from 'store/useNotificationStore'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons'
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons'

import { createNonLeaseAccordions, createLeaseAccordions } from 'utils/accordion-util'
import { getDisclosures } from 'utils/disclosures-util'
import { numberFormat } from 'utils/number-format-util'
import { convertUTCToLocalDate } from 'utils/date-util'
import { pdsAnalytics } from 'analytics/pdsAnalytics'
import { PRICE_COMPARISON_CHANGES_ENBALED, TRADEMARK_SYMBOL } from 'constants/pdsConstant'
import { usePdsInfo } from 'hooks/usePDSInfo'
import { useBannerTypeCheck } from 'hooks/useBannerTypeCheck'
import { useTranslation } from 'react-i18next'
import {
    IS_NEW_LAYOUT_ENABLED,
    VECHILE_CONTAINER_WIDTH,
    NEW_ACCORDIAN_WIDTH,
    NEW_RES_ACCORDIAN_WIDTH
} from 'constants/layoutConstant'
import NewLayout from 'components/Accordion/NewLayout'
import OldLayout from 'components/Accordion/OldLayout'

const disclosures = getDisclosures()

const PdsPage = () => {
    const { t } = useTranslation()
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const { show } = useNotificationStore((state) => state)
    const {
        vehicleImage: imageUrl,
        isStatusPending,
        isStatusRejected,
        isStatusAccepted,
        getPdsStatus
    } = useDynamicManageStore((state) => state)

    const { data: dealData, isLoading } = useDealData()

    const [openDialog, setOpenDialog] = useState(false)

    //Destructure
    const {
        data: {
            vehicle: { year, model, vin },
            customer: { firstName = '' },
            vehicleFinancing: {
                monthlyPayment,
                annualPercentageRate,
                term,
                annualMileage,
                monthlyPaymentsDue,
                secondMonthlyPaymentsDue
            } = {},
            dueAtSigning: { totalAmountsDueAtLeaseSigningOrDelivery } = {},
            timeStamp,
            amountFinanced,
            transactionType,
            cashDueAtDealer,
            orderType,
            priceComparisonResponse,
            document,
            dealerName,
            dealerPhoneNumber
        } = {
            vehicle: {},
            customer: {},
            dealerInfo: {},
            vehicleFinancing: {},
            taxesAndFees: {},
            dueAtSigning: {},
            orderType: '',
            priceComparisonResponse: {},
            document: {}
        }
    } = dealData || {}

    let formatTimeStamp = ''
    if (timeStamp) {
        formatTimeStamp = convertUTCToLocalDate(timeStamp)
    }

    useBannerTypeCheck(orderType, dealData, priceComparisonResponse)

    let accordions = []

    if (dealData) {
        accordions = transactionType !== 'Lease' ? createNonLeaseAccordions(dealData) : createLeaseAccordions(dealData)
    }

    const closeDialog = () => {
        setOpenDialog(false)
    }
    const showDialog = () => {
        pdsAnalytics.track('viewDisclosures')
        setOpenDialog(true)
    }

    const { infoNotify, sortedDiscloures } = usePdsInfo(
        { ...priceComparisonResponse, documentFee: document },
        dealerName,
        dealerPhoneNumber,
        transactionType
    )

    if (isLoading) {
        return <Spinner />
    }

    return (
        <PdsLayout theme="light">
            {show && <Snackbar />}

            <Typography
                data-testid="pre-delivery-summary"
                sx={{
                    typography: { xs: 'smallHeadline4', sm: 'smallHeadline3', md: 'smallHeadline0' },
                    fontWeight: 600,
                    mx: { xs: 3, md: 0 }
                }}
            >
                {t('pre_delivery_summary')}
            </Typography>

            {PRICE_COMPARISON_CHANGES_ENBALED && infoNotify}

            {IS_NEW_LAYOUT_ENABLED && (
                <Box
                    sx={{
                        maxWidth: VECHILE_CONTAINER_WIDTH,
                        m: '0 auto',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: 10, smd: 12 }
                    }}
                >
                    <VehicleDetails
                        vehicleInfo={dealData?.data?.vehicle}
                        dealerName={dealerName}
                        dealerPhoneNumber={dealerPhoneNumber}
                    />

                    <PurchaseDetails dealData={dealData} />

                    {/* new layout */}
                    <Box
                        sx={{
                            display: 'grid',
                            mx: { xs: 3, smd: 'auto' },
                            maxWidth: { xs: 'initial', smd: NEW_RES_ACCORDIAN_WIDTH, md: NEW_ACCORDIAN_WIDTH },
                            width: { xs: 'initial', smd: '100%' }
                        }}
                    >
                        <NewLayout dealData={dealData} />
                    </Box>
                </Box>
            )}

            {!IS_NEW_LAYOUT_ENABLED && (
                <>
                    <Box
                        color={isStatusRejected() ? 'error.main' : 'success.main'}
                        sx={{
                            display: { xs: 'grid', md: 'flex' },
                            justifyContent: {
                                xs: 'space-between',
                                md: isStatusAccepted() ? 'space-between' : 'flex-end'
                            },
                            gap: { xs: 6, md: 0 },
                            mx: { xs: 3, md: 0 }
                        }}
                    >
                        {isStatusAccepted() && (
                            <Typography
                                variant="mediumSubTitle"
                                letterSpacing="1px"
                                textTransform="capitalize"
                                data-testid="thank"
                            >
                                {t('thank_you', { name: firstName })}
                            </Typography>
                        )}

                        <Box display="flex" gap="5px" alignItems={'center'}>
                            {!isStatusPending() && (
                                <>
                                    {isStatusAccepted() && <FontAwesomeIcon icon={faCircleCheck} />}
                                    {isStatusRejected() && <FontAwesomeIcon icon={faCircleXmark} />}
                                    <Typography variant="boldBody1" letterSpacing="1px" data-testid="status">
                                        {getPdsStatus()}
                                    </Typography>
                                </>
                            )}
                            {isStatusPending() && (
                                <Typography
                                    variant="regularBody1"
                                    color="text.primary"
                                    letterSpacing="1px"
                                    data-testid="time-stamp"
                                >
                                    {formatTimeStamp}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {!isStatusAccepted() && (
                        <Typography
                            variant="regularBody1"
                            color="text.secondary"
                            data-testid="review"
                            sx={{
                                letterSpacing: '1px',
                                mx: { xs: 3, md: 0 }
                            }}
                        >
                            {t('sub_heading')}
                        </Typography>
                    )}

                    <Box
                        sx={{
                            display: { xs: 'grid', md: 'flex' },
                            gap: 6,
                            justifyContent: { md: 'space-between' },
                            alignItems: 'flex-start'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 3,
                                mx: { xs: 3, md: 0 }
                            }}
                        >
                            <Typography variant="boldTitle" fontWeight={600} data-testid="model">
                                {`${year} ${model} ${TRADEMARK_SYMBOL}`}
                            </Typography>
                            <Typography variant="regularSubTitle" data-testid="vin">
                                {t('vin', { vin: vin })}
                            </Typography>
                            <Typography variant="regularBody2" component="span" data-testid="dealer-name">
                                {t('vehicle_ordered_from')}&nbsp;
                                <Typography
                                    variant="regularBody2"
                                    component="span"
                                    className="text-with-underline-item"
                                >
                                    {dealerName}
                                </Typography>
                                {dealerPhoneNumber && (
                                    <Typography
                                        variant="regularBody2"
                                        component="span"
                                        sx={{
                                            color: 'primary.main',
                                            textTransform: 'capitalize'
                                        }}
                                    >
                                        <>
                                            &nbsp;|&nbsp;
                                            <Link
                                                href={`tel:${dealerPhoneNumber}`}
                                                onClick={() => pdsAnalytics.track('callDealer')}
                                                className="text-with-underline-item"
                                                sx={{
                                                    borderBottom: (theme) => `1px solid ${theme.palette.primary.main}`,
                                                    textDecoration: 'none'
                                                }}
                                            >
                                                {dealerPhoneNumber}
                                            </Link>
                                        </>
                                    </Typography>
                                )}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                lineHeight: 0,
                                textAlign: 'center'
                            }}
                        >
                            <img
                                src={imageUrl}
                                alt={'car'}
                                loading="lazy"
                                width={280}
                                height={140}
                                data-testid="image"
                            />
                            <Box mt={2}>
                                <Badge badgeContent={disclosures.vehicleImage.order}></Badge>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            backgroundColor: 'background.paper',
                            borderRadius: 1.25,
                            display: 'grid',
                            justifyItems: 'center',
                            gap: 6,
                            px: 5,
                            py: 6,
                            mx: { xs: 3, md: 0 }
                        }}
                    >
                        <Typography variant="boldBody1" color="black" data-testid="transaction-type">
                            {transactionType === 'Cash' && t('cash_purchase_details')}
                            {transactionType === 'Retail' && t('ford_credit_financing_details')}
                            {transactionType === 'FlexBuy' && t('flexbuy_details')}
                            {transactionType === 'Lease' && t('leasing_details')}
                            {transactionType === 'Financing' && t('finance_details')}
                        </Typography>

                        <Box
                            sx={{
                                display: { xs: 'grid', md: 'flex' },
                                gap: { xs: 6, lg: 16 },
                                justifyItems: 'center'
                            }}
                        >
                            {transactionType === 'Lease' && (
                                <>
                                    <Box
                                        tabIndex={0}
                                        sx={{
                                            display: 'grid',
                                            alignItems: 'end',
                                            justifyItems: { xs: 'center', md: 'flex-start' }
                                        }}
                                    >
                                        <Typography
                                            variant="regularBody3"
                                            color="text.secondary"
                                            data-testid="monthly-payment-label"
                                            sx={{
                                                typography: { md: 'regularBody2' },
                                                display: 'inline-flex',
                                                pl: 3.5
                                            }}
                                        >
                                            {disclosures.monthlyPayment.name}
                                            <Badge badgeContent={disclosures.monthlyPayment.order}></Badge>
                                        </Typography>
                                        <Box
                                            tabIndex={0}
                                            data-testid="monthly-payment-value"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: '5px',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Typography variant="regularBody2" component="span" mb="auto">
                                                $
                                            </Typography>
                                            <Typography
                                                variant="smallHeadline3"
                                                component="span"
                                                color="secondary.main"
                                                fontWeight="bold"
                                                sx={{
                                                    typography: { lg: 'smallDisplay3' }
                                                }}
                                            >
                                                {numberFormat(monthlyPayment)}
                                            </Typography>
                                            <Typography variant="regularBody2" component="span">
                                                /{t('mo')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            flexDirection: 'row',
                                            justifyContent: 'space-between',
                                            gap: { xs: 6, lg: 16 }
                                        }}
                                    >
                                        <Box
                                            tabIndex={0}
                                            sx={{
                                                display: 'grid',
                                                alignItems: 'end',
                                                justifyItems: { xs: 'center', md: 'flex-start' }
                                            }}
                                        >
                                            <Typography
                                                variant="regularBody3"
                                                color="text.secondary"
                                                data-testid="term-label"
                                                sx={{
                                                    typography: { md: 'regularBody2' },
                                                    display: 'inline-flex'
                                                }}
                                            >
                                                {t('term')}
                                                <Badge badgeContent={disclosures.term.order}></Badge>
                                            </Typography>
                                            <Box
                                                tabIndex={0}
                                                data-testid="term-value"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'baseline',
                                                    gap: '5px',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <Typography
                                                    variant="smallHeadline3"
                                                    component="span"
                                                    color="secondary.main"
                                                    fontWeight="bold"
                                                    sx={{
                                                        typography: { lg: 'smallDisplay3' }
                                                    }}
                                                >
                                                    {term}
                                                </Typography>
                                                <Typography variant="regularBody2" component="span">
                                                    {t('mos')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box
                                            tabIndex={0}
                                            sx={{
                                                display: 'grid',
                                                alignItems: 'end',
                                                justifyItems: { xs: 'center', md: 'flex-start' }
                                            }}
                                        >
                                            <Typography
                                                variant="regularBody3"
                                                color="text.secondary"
                                                data-testid="mileage-label"
                                                sx={{
                                                    typography: { md: 'regularBody2' },
                                                    display: 'inline-flex'
                                                }}
                                            >
                                                {t('mileage')}
                                                <Badge badgeContent={disclosures.annualMileage.order}></Badge>
                                            </Typography>
                                            <Box
                                                tabIndex={0}
                                                data-testid="mileage-value"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'baseline',
                                                    gap: '5px',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <Typography
                                                    variant="smallHeadline3"
                                                    component="span"
                                                    color="secondary.main"
                                                    fontWeight="bold"
                                                    sx={{
                                                        typography: { lg: 'smallDisplay3' }
                                                    }}
                                                >
                                                    {numberFormat(annualMileage)}
                                                </Typography>
                                                <Typography variant="regularBody2" component="span">
                                                    /{t('yr')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box
                                        tabIndex={0}
                                        sx={{
                                            display: 'grid',
                                            alignItems: 'end',
                                            justifyItems: { xs: 'center', md: 'flex-start' }
                                        }}
                                    >
                                        <Typography
                                            variant="regularBody3"
                                            color="text.secondary"
                                            data-testid="amount-due-signing-label"
                                            sx={{
                                                typography: { md: 'regularBody2' },
                                                display: 'inline-flex',
                                                pl: 3.5
                                            }}
                                        >
                                            {t('amount_due_at_signing')}
                                            <Badge
                                                badgeContent={disclosures.totalAmountsDueAtLeaseSigningOrDelivery.order}
                                            ></Badge>
                                        </Typography>
                                        <Box
                                            tabIndex={0}
                                            data-testid="amount-due-signing-value"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: '5px',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Typography variant="regularBody2" component="span" mb="auto">
                                                $
                                            </Typography>
                                            <Typography
                                                variant="smallHeadline3"
                                                component="span"
                                                color="secondary.main"
                                                fontWeight="bold"
                                                sx={{
                                                    typography: { lg: 'smallDisplay3' }
                                                }}
                                            >
                                                {numberFormat(totalAmountsDueAtLeaseSigningOrDelivery)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </>
                            )}

                            {transactionType === 'Retail' && (
                                <>
                                    <Box
                                        tabIndex={0}
                                        sx={{
                                            display: 'grid',
                                            alignItems: 'end',
                                            justifyItems: { xs: 'center', md: 'flex-start' }
                                        }}
                                    >
                                        <Typography
                                            variant="regularBody3"
                                            color="text.secondary"
                                            data-testid="amount-financed-label"
                                            sx={{
                                                typography: { md: 'regularBody2' },
                                                display: 'inline-flex',
                                                pl: 3.5
                                            }}
                                        >
                                            {disclosures.amountFinanced.name}
                                            <Badge badgeContent={disclosures.amountFinanced.order}></Badge>
                                        </Typography>
                                        <Box
                                            tabIndex={0}
                                            data-testid="amount-financed-value"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: '5px',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Typography variant="regularBody2" component="span" mb="auto">
                                                $
                                            </Typography>
                                            <Typography
                                                variant="smallHeadline3"
                                                component="span"
                                                color="secondary.main"
                                                fontWeight="bold"
                                                sx={{
                                                    typography: { lg: 'smallDisplay3' }
                                                }}
                                            >
                                                {numberFormat(amountFinanced)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        tabIndex={0}
                                        sx={{
                                            display: 'grid',
                                            alignItems: 'end',
                                            justifyItems: { xs: 'center', md: 'flex-start' }
                                        }}
                                    >
                                        <Typography
                                            variant="regularBody3"
                                            color="text.secondary"
                                            data-testid="monthly-payment-label"
                                            sx={{
                                                typography: { md: 'regularBody2' },
                                                display: 'inline-flex',
                                                pl: 3.5
                                            }}
                                        >
                                            {disclosures.monthlyPayment.name}
                                            <Badge badgeContent={disclosures.monthlyPayment.order}></Badge>
                                        </Typography>
                                        <Box
                                            tabIndex={0}
                                            data-testid="monthly-payment-value"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: '5px'
                                            }}
                                        >
                                            <Typography variant="regularBody2" component="span" mb="auto">
                                                $
                                            </Typography>
                                            <Typography
                                                variant="smallHeadline3"
                                                component="span"
                                                color="secondary.main"
                                                fontWeight="bold"
                                                sx={{
                                                    typography: { lg: 'smallDisplay3' }
                                                }}
                                            >
                                                {numberFormat(monthlyPayment)}
                                            </Typography>
                                            <Typography variant="regularBody2" component="span">
                                                /{t('mo')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            flexWrap: 'wrap',
                                            justifyContent: 'center',
                                            gap: { xs: 6, lg: 16 }
                                        }}
                                    >
                                        <Box
                                            tabIndex={0}
                                            sx={{
                                                display: 'grid',
                                                alignItems: 'end',
                                                justifyItems: { xs: 'center', md: 'flex-start' }
                                            }}
                                        >
                                            <Typography
                                                variant="regularBody3"
                                                color="text.secondary"
                                                data-testid="apr-label"
                                                sx={{
                                                    typography: { md: 'regularBody2' },
                                                    display: 'inline-flex'
                                                }}
                                            >
                                                {disclosures.annualPercentageRate.name}
                                                <Badge badgeContent={disclosures.annualPercentageRate.order}></Badge>
                                            </Typography>
                                            <Box
                                                tabIndex={0}
                                                data-testid="apr-value"
                                                sx={{
                                                    display: 'flex',
                                                    gap: '5px'
                                                }}
                                            >
                                                <Typography
                                                    variant="smallHeadline3"
                                                    component="span"
                                                    color="secondary.main"
                                                    fontWeight="bold"
                                                    sx={{
                                                        typography: { lg: 'smallDisplay3' }
                                                    }}
                                                >
                                                    {annualPercentageRate}
                                                </Typography>
                                                <Typography variant="regularBody2" component="span">
                                                    %
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box
                                            tabIndex={0}
                                            sx={{
                                                display: 'grid',
                                                alignItems: 'end',
                                                justifyItems: { xs: 'center', md: 'flex-start' }
                                            }}
                                        >
                                            <Typography
                                                variant="regularBody3"
                                                color="text.secondary"
                                                data-testid="term-label"
                                                sx={{
                                                    typography: { md: 'regularBody2' },
                                                    display: 'inline-flex'
                                                }}
                                            >
                                                {t('term')}
                                                <Badge badgeContent={disclosures.term.order}></Badge>
                                            </Typography>
                                            <Box
                                                tabIndex={0}
                                                data-testid="term-value"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'baseline',
                                                    gap: '5px'
                                                }}
                                            >
                                                <Typography
                                                    variant="smallHeadline3"
                                                    component="span"
                                                    color="secondary.main"
                                                    fontWeight="bold"
                                                    sx={{
                                                        typography: { lg: 'smallDisplay3' }
                                                    }}
                                                >
                                                    {term}
                                                </Typography>
                                                <Typography variant="regularBody2" component="span">
                                                    {t('mos')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </>
                            )}

                            {transactionType === 'FlexBuy' && (
                                <>
                                    <Box
                                        tabIndex={0}
                                        sx={{
                                            display: 'grid',
                                            alignItems: 'end',
                                            justifyItems: { xs: 'center', md: 'flex-start' }
                                        }}
                                    >
                                        <Typography
                                            variant="regularBody3"
                                            color="text.secondary"
                                            data-testid="amount-financed-label"
                                            sx={{
                                                typography: { md: 'regularBody2' },
                                                display: 'inline-flex',
                                                pl: 3.5
                                            }}
                                        >
                                            {disclosures.amountFinanced.name}
                                            <Badge badgeContent={disclosures.amountFinanced.order}></Badge>
                                        </Typography>
                                        <Box
                                            tabIndex={0}
                                            data-testid="amount-financed-value"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: '5px',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Typography variant="regularBody2" component="span" mb="auto">
                                                $
                                            </Typography>
                                            <Typography
                                                variant="smallHeadline3"
                                                component="span"
                                                color="secondary.main"
                                                fontWeight="bold"
                                                sx={{
                                                    typography: { lg: 'smallDisplay3' }
                                                }}
                                            >
                                                {numberFormat(amountFinanced)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            flexDirection: 'row',
                                            gap: { xs: 6, lg: 16 },
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Box
                                            tabIndex={0}
                                            sx={{
                                                display: 'grid',
                                                alignItems: 'end',
                                                justifyItems: { xs: 'center', md: 'flex-start' }
                                            }}
                                        >
                                            <Typography
                                                variant="regularBody3"
                                                color="text.secondary"
                                                data-testid="monthly-payment-1-label"
                                                sx={{
                                                    typography: { md: 'regularBody2' },
                                                    display: 'inline-flex',
                                                    pl: 3.5
                                                }}
                                            >
                                                {t('monthly_payment')} (1-36)
                                                <Badge badgeContent={disclosures.monthlyPaymentsDue.order}></Badge>
                                            </Typography>
                                            <Box
                                                tabIndex={0}
                                                data-testid="monthly-payment-1-value"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'baseline',
                                                    gap: '5px',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <Typography variant="regularBody2" component="span" mb="auto">
                                                    $
                                                </Typography>
                                                <Typography
                                                    variant="smallHeadline3"
                                                    component="span"
                                                    color="secondary.main"
                                                    fontWeight="bold"
                                                    sx={{
                                                        typography: { lg: 'smallDisplay3' }
                                                    }}
                                                >
                                                    {numberFormat(monthlyPaymentsDue)}
                                                </Typography>
                                                <Typography variant="regularBody2" component="span">
                                                    /{t('mo')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box
                                            tabIndex={0}
                                            sx={{
                                                display: 'grid',
                                                alignItems: 'end',
                                                justifyItems: { xs: 'center', md: 'flex-start' }
                                            }}
                                        >
                                            <Typography
                                                variant="regularBody3"
                                                color="text.secondary"
                                                data-testid="monthly-payment-2-label"
                                                sx={{
                                                    typography: { md: 'regularBody2' },
                                                    display: 'inline-flex',
                                                    pl: 3.5
                                                }}
                                            >
                                                {t('monthly_payment')} (37-{term})
                                                <Badge
                                                    badgeContent={disclosures.secondMonthlyPaymentsDue.order}
                                                ></Badge>
                                            </Typography>
                                            <Box
                                                tabIndex={0}
                                                data-testid="monthly-payment-2-value"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'baseline',
                                                    gap: '5px',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <Typography variant="regularBody2" component="span" mb="auto">
                                                    $
                                                </Typography>
                                                <Typography
                                                    variant="smallHeadline3"
                                                    component="span"
                                                    color="secondary.main"
                                                    fontWeight="bold"
                                                    sx={{
                                                        typography: { lg: 'smallDisplay3' }
                                                    }}
                                                >
                                                    {numberFormat(secondMonthlyPaymentsDue)}
                                                </Typography>
                                                <Typography variant="regularBody2" component="span">
                                                    /{t('mo')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box
                                        tabIndex={0}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            flexWrap: 'wrap',
                                            gap: { xs: 6, lg: 16 },
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                alignItems: 'end',
                                                justifyItems: { xs: 'center', md: 'flex-start' }
                                            }}
                                        >
                                            <Typography
                                                variant="regularBody3"
                                                color="text.secondary"
                                                data-testid="apr-label"
                                                sx={{
                                                    typography: { md: 'regularBody2' },
                                                    display: 'inline-flex'
                                                }}
                                            >
                                                {disclosures.annualPercentageRate.name}
                                                <Badge badgeContent={disclosures.annualPercentageRate.order}></Badge>
                                            </Typography>
                                            <Box
                                                tabIndex={0}
                                                data-testid="apr-value"
                                                sx={{
                                                    display: 'flex',
                                                    gap: '5px',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <Typography
                                                    variant="smallHeadline3"
                                                    component="span"
                                                    color="secondary.main"
                                                    fontWeight="bold"
                                                    sx={{
                                                        typography: { lg: 'smallDisplay3' }
                                                    }}
                                                >
                                                    {annualPercentageRate}
                                                </Typography>
                                                <Typography variant="regularBody2" component="span">
                                                    %
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </>
                            )}

                            {transactionType === 'Cash' && (
                                <Box
                                    tabIndex={0}
                                    sx={{
                                        display: 'grid',
                                        alignItems: 'end',
                                        justifyItems: 'center'
                                    }}
                                >
                                    <Typography
                                        variant="regularBody3"
                                        color="text.secondary"
                                        data-testid="amount-due-at-dealer-label"
                                        sx={{
                                            typography: { md: 'regularBody2' },
                                            display: 'inline-flex',
                                            pl: 3.5
                                        }}
                                    >
                                        {disclosures.cashDueAtDealer.name}
                                        <Badge badgeContent={disclosures.cashDueAtDealer.order}></Badge>
                                    </Typography>
                                    <Box
                                        tabIndex={0}
                                        data-testid="amount-due-at-dealer-value"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            gap: '5px',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Typography variant="regularBody2" component="span" mb="auto">
                                            $
                                        </Typography>
                                        <Typography
                                            variant="smallHeadline3"
                                            component="span"
                                            color="secondary.main"
                                            fontWeight="bold"
                                            sx={{
                                                typography: { lg: 'smallDisplay3' }
                                            }}
                                        >
                                            {numberFormat(cashDueAtDealer)}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {transactionType === 'Financing' && (
                                <Box display="grid" gap={6} justifyItems="center">
                                    <Box tabIndex={0} display="grid" alignItems="end" justifyItems="center">
                                        <Typography
                                            variant="regularBody3"
                                            color="text.secondary"
                                            data-testid="amount-financed-label"
                                            sx={{
                                                typography: { md: 'regularBody2' },
                                                display: 'inline-flex',
                                                pl: 3.5
                                            }}
                                        >
                                            {disclosures.amountFinanced.name}
                                            <Badge badgeContent={disclosures.amountFinanced.order}></Badge>
                                        </Typography>
                                        <Box
                                            tabIndex={0}
                                            data-testid="amount-financed-value"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: '5px',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Typography variant="regularBody2" component="span" mb="auto">
                                                $
                                            </Typography>
                                            <Typography
                                                variant="smallHeadline3"
                                                component="span"
                                                color="secondary.main"
                                                fontWeight="bold"
                                                sx={{
                                                    typography: { lg: 'smallDisplay3' }
                                                }}
                                            >
                                                {numberFormat(amountFinanced)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography
                                        tabIndex={0}
                                        variant="regularBody3"
                                        color="common.black.main"
                                        data-testid="more-information"
                                        sx={{
                                            typography: { md: 'regularBody3' },
                                            display: 'inline-flex',
                                            textAlign: 'center'
                                        }}
                                    >
                                        {t('check_with_dealer')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </>
            )}

            {/* old layout */}
            {!IS_NEW_LAYOUT_ENABLED && (
                <Box
                    sx={{
                        display: 'grid',
                        gap: 6,
                        mx: { md: 3, lg: 'auto' },
                        width: { lg: 710 }
                    }}
                >
                    <OldLayout dealData={dealData} accordions={accordions} />
                </Box>
            )}

            <Box
                display="flex"
                justifyContent="center"
                sx={{
                    marginTop: { xs: 0, md: 6 }
                }}
            >
                <Link
                    component="button"
                    onClick={showDialog}
                    data-testid="disclosures"
                    className="text-with-underline-item"
                    sx={{
                        textAlign: 'center',
                        textDecoration: 'underline',
                        typography: 'boldBody2',
                        color: 'common.link.light'
                    }}
                >
                    {t('disclosures')}
                </Link>
                {openDialog && sortedDiscloures?.length && (
                    <DisclosureDialog
                        isDialogOpen={openDialog}
                        closeDialog={closeDialog}
                        sortedDiscloures={sortedDiscloures}
                    />
                )}
            </Box>
        </PdsLayout>
    )
}

export default PdsPage
